/*
 * CredentialingSubmissionsPanel — god-mode ecosystem view
 *
 * The single workspace where an admin drives every TP + driver from
 * "not_started" → "cleared". Two tabs:
 *   • TPs    — every transportation provider in the network
 *   • Drivers — every driver (TP-owned + ASTAT-independent)
 *
 * Each row shows:
 *   - Derived state (not_started/incomplete/ready_to_submit/submitted/in_review/cleared/rejected/lapsed)
 *   - Missing-field count
 *   - MedexOva status
 * Click any row → drawer with EVERY field editable, missing highlighted,
 * "Submit to MedexOva" + "Retry" actions.
 */
import RequestDocsButton from "./RequestDocsButton";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { apiClient } from "../../lib/api";
import { credentialingSpec } from "./credentialingFields";
import { ShieldCheck, AlertTriangle, CheckCircle2, XCircle, Clock, RefreshCw, X, Send, UserPlus } from "lucide-react";

const TP = "/tpt";

// FastAPI returns `detail` as a plain string for our own HTTPExceptions, but
// as an ARRAY of {type, loc, msg, input, url} objects for raw pydantic
// validation errors — which MedexOva's own sandbox API returns verbatim and
// we pass straight through as `data.error`/`e.response.data.detail`. Passing
// that array directly into toast.error() crashes the whole app (React error
// #31 — "objects are not valid as a React child"). Always go through this
// first.
function errorDetailToText(detail, fallback) {
  if (!detail) return fallback;
  let text;
  if (typeof detail === "string") text = detail;
  else if (Array.isArray(detail)) {
    const parts = detail.map((d) => {
      if (typeof d === "string") return d;
      const loc = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
      return [loc, d?.msg || d?.type].filter(Boolean).join(": ");
    });
    text = parts.join("; ") || fallback;
  } else if (typeof detail === "object") text = detail.msg || detail.message || JSON.stringify(detail);
  else text = String(detail);
  return text.length > 200 ? text.slice(0, 200) + "…" : text;
}

const STATE_META = {
  not_started:       { label: "Not started",     tone: "slate",  Icon: Clock },
  incomplete:        { label: "Incomplete",      tone: "amber",  Icon: AlertTriangle },
  ready_to_submit:   { label: "Ready to submit", tone: "blue",   Icon: Send },
  submitted:         { label: "Submitted",       tone: "blue",   Icon: Clock },
  in_review:         { label: "In review",       tone: "amber",  Icon: Clock },
  cleared:           { label: "Cleared",         tone: "green",  Icon: CheckCircle2 },
  rejected:          { label: "Rejected",        tone: "red",    Icon: XCircle },
  lapsed:            { label: "Lapsed",          tone: "red",    Icon: XCircle },
};
const TONE_CLS = {
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  amber: "bg-amber-100 text-amber-900 border-amber-200",
  blue:  "bg-blue-100 text-blue-800 border-blue-200",
  green: "bg-green-100 text-green-800 border-green-200",
  red:   "bg-red-100 text-red-800 border-red-200",
};

function StateChip({ state }) {
  const meta = STATE_META[state] || STATE_META.not_started;
  const { Icon } = meta;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md border ${TONE_CLS[meta.tone]}`} data-testid={`state-${state}`}>
      <Icon size={11}/> {meta.label}
    </span>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

// Sent-at / Received-at / Awaiting / Stalled / Failed — one glance, never a
// silent guess about whether a dossier actually reached MedexOva.
function MedexOvaTimeline({ row, statusText, testIdPrefix, sentKey = "_sent_at", receivedKey = "_received_at", errorKey = "_send_error", stalledKey = "_stalled" }) {
  const sentAt = row[sentKey], receivedAt = row[receivedKey], sendError = row[errorKey], stalled = row[stalledKey];
  if (sendError) {
    return (
      <div data-testid={`${testIdPrefix}-failed`}>
        <div className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
          <XCircle size={11}/> Send failed
        </div>
        <div className="text-[10px] text-red-600 mt-0.5 max-w-[220px] truncate" title={typeof sendError === "string" ? sendError : JSON.stringify(sendError)}>
          {errorDetailToText(sendError?.detail, String(sendError))}
        </div>
      </div>
    );
  }
  if (receivedAt) {
    return (
      <div data-testid={`${testIdPrefix}-received`}>
        <div className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded px-1.5 py-0.5">
          <CheckCircle2 size={11}/> Received {fmtTime(receivedAt)}
        </div>
        {statusText && <div className="text-[10px] text-slate-500 mt-0.5">{statusText}</div>}
      </div>
    );
  }
  if (sentAt) {
    return stalled ? (
      <div data-testid={`${testIdPrefix}-stalled`}>
        <div className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-900 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5">
          <AlertTriangle size={11}/> Stalled — sent {fmtTime(sentAt)}
        </div>
        <div className="text-[10px] text-amber-700 mt-0.5">No confirmation &gt;48h — follow up</div>
      </div>
    ) : (
      <div data-testid={`${testIdPrefix}-awaiting`}>
        <div className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
          <Clock size={11}/> Awaiting MedexOva
        </div>
        <div className="text-[10px] text-slate-500 mt-0.5">Sent {fmtTime(sentAt)}</div>
      </div>
    );
  }
  return <span className="text-slate-400 text-xs">—</span>;
}

export default function CredentialingSubmissionsPanel({ focusKind, focusId, onFocusConsumed } = {}) {
  const [kind, setKind] = useState(focusKind || "tp");
  const [rows, setRows] = useState([]);
  const [stalledCount, setStalledCount] = useState(0);
  const [stalledInsuranceCount, setStalledInsuranceCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // full record with _missing[] etc

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`${TP}/credentialing/submissions`, { params: { kind } });
      setRows(data.rows || []);
      setStalledCount(data.stalled_count || 0);
      setStalledInsuranceCount(data.stalled_insurance_count || 0);
    } catch (e) { toast.error(errorDetailToText(e?.response?.data?.detail, "Failed to load submissions")); }
    finally { setLoading(false); }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  // Phase 2: jump straight to the record just approved in New Applications,
  // instead of making the admin go find it in the list themselves.
  useEffect(() => {
    if (!focusId || loading || rows.length === 0) return;
    const match = rows.find(r => (r.tp_id || r.id) === focusId);
    if (match) {
      setEditing(match);
      onFocusConsumed?.();
    }
  }, [focusId, loading, rows, onFocusConsumed]);

  const counts = useMemo(() => {
    const c = { total: rows.length };
    Object.keys(STATE_META).forEach(k => { c[k] = 0; });
    rows.forEach(r => { const s = r._submission_state; if (c[s] != null) c[s]++; });
    return c;
  }, [rows]);

  return (
    <div data-testid="credentialing-submissions-panel">
      <div className="flex items-center gap-3 mb-4">
        <ShieldCheck className="w-8 h-8 text-forest"/>
        <div>
          <h2 className="text-xl font-bold text-slate_navy">Credentialing Submissions</h2>
          <p className="text-sm text-slate-500">God-mode workspace. Every TP + driver, their submission state, missing fields, and one-click submit-to-MedexOva.</p>
        </div>
      </div>

      {(stalledCount > 0 || stalledInsuranceCount > 0) && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="stalled-rollup-banner">
          <AlertTriangle size={16} className="shrink-0"/>
          <span>
            {stalledCount > 0 && <><b>{stalledCount}</b> credentialing submission{stalledCount !== 1 ? "s" : ""} stalled</>}
            {stalledCount > 0 && stalledInsuranceCount > 0 && " and "}
            {stalledInsuranceCount > 0 && <><b>{stalledInsuranceCount}</b> insurance submission{stalledInsuranceCount !== 1 ? "s" : ""} stalled</>}
            {" "}— sent to MedexOva more than 48h ago with no confirmation back yet.
          </span>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        {[
          { k: "tp", l: "Transportation Providers" },
          { k: "driver", l: "Drivers" },
        ].map(o => (
          <button key={o.k} onClick={() => setKind(o.k)}
            className={`text-sm px-3 py-1.5 rounded-md ${kind === o.k ? "bg-forest text-white" : "border border-[#E2E0D8]"}`}
            data-testid={`sub-kind-${o.k}`}>{o.l}</button>
        ))}
        <div className="flex-1"/>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1 border border-[#E2E0D8] px-3 py-1.5 rounded-md text-xs" data-testid="sub-refresh">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""}/> Refresh
        </button>
      </div>

      {/* Status counts */}
      <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mb-4 text-xs">
        {Object.entries(STATE_META).map(([k, m]) => (
          <div key={k} className={`px-2 py-1.5 rounded-md border text-center ${TONE_CLS[m.tone]}`} data-testid={`sub-count-${k}`}>
            <div className="text-[10px] font-semibold opacity-80">{m.label}</div>
            <div className="text-lg font-bold">{counts[k] || 0}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-[#E2E0D8] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="py-2 px-3">Name</th>
              {kind === "driver" && <th className="py-2 px-3">Parent TP</th>}
              <th className="py-2 px-3">State</th>
              <th className="py-2 px-3">MedexOva</th>
              {kind === "driver" && <th className="py-2 px-3">Insurance</th>}
              <th className="py-2 px-3">Missing</th>
              <th className="py-2 px-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1EFE8]">
            {rows.length === 0 && !loading && (
              <tr><td colSpan={kind === "driver" ? 7 : 6} className="px-3 py-8 text-center text-slate-500">No {kind}s in the network yet.</td></tr>
            )}
            {rows.map(r => (
              <tr key={r.tp_id || r.id} className="hover:bg-slate-50" data-testid={`sub-row-${r.tp_id || r.id}`}>
                <td className="py-2 px-3 font-medium">{r._display_name}</td>
                {kind === "driver" && <td className="py-2 px-3 text-slate-600 text-xs">{r._tp_name}</td>}
                <td className="py-2 px-3"><StateChip state={r._submission_state}/></td>
                <td className="py-2 px-3">
                  <MedexOvaTimeline row={r} statusText={(kind === "tp" ? r.medexova_tp_status : r.medexova_credentialing_status) || ""} testIdPrefix={`sub-medexova-${r.tp_id || r.id}`}/>
                </td>
                {kind === "driver" && (
                  <td className="py-2 px-3">
                    <MedexOvaTimeline row={r} statusText={r.medexova_insurance_status || ""} testIdPrefix={`sub-insurance-${r.id}`}
                      sentKey="_ins_sent_at" receivedKey="_ins_received_at" errorKey="_ins_send_error" stalledKey="_ins_stalled"/>
                  </td>
                )}
                <td className="py-2 px-3">
                  {r._missing_count > 0
                    ? <span className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">{r._missing_count} missing</span>
                    : <CheckCircle2 size={14} className="text-green-600"/>}
                </td>
                <td className="py-2 px-3 text-right">
                  <button onClick={() => setEditing(r)} className="border border-[#E2E0D8] text-xs px-3 py-1 rounded-md hover:bg-slate-50" data-testid={`sub-open-${r.tp_id || r.id}`}>
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <SubmissionDrawer
          kind={kind}
          record={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SubmissionDrawer({ kind, record, onClose, onSaved }) {
  const spec = credentialingSpec[kind];
  const missing = new Set(record._missing || []);
  const [form, setForm] = useState(() => {
    const initial = {};
    spec.forEach(f => { initial[f.key] = record[f.key] ?? ""; });
    return initial;
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      // Coerce empty strings to null so we don't clobber Mongo with blanks
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      );
      const endpoint = kind === "tp"
        ? `${TP}/providers/${record.tp_id}`
        : `/drivers/${record.id}`;
      await apiClient.patch(endpoint, payload);
      toast.success("Saved");
      onSaved?.();
    } catch (e) { toast.error(errorDetailToText(e?.response?.data?.detail, "Save failed")); }
    finally { setBusy(false); }
  };

  const submitToMedexOva = async () => {
    setBusy(true);
    try {
      const endpoint = kind === "tp"
        ? `/medexova/tps/${record.tp_id}/submit`
        : `/medexova/drivers/${record.id}/submit`;
      const { data } = await apiClient.post(endpoint);
      if (data.ok) toast.success(`Submitted to MedexOva · status: ${data.status || "pending"}`);
      else toast.error(errorDetailToText(data.error?.detail, "Submission rejected"));
      onSaved?.();
    } catch (e) {
      toast.error(errorDetailToText(e?.response?.data?.detail, "Submit failed"));
    } finally { setBusy(false); }
  };

  const submitInsurance = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/medexova/drivers/${record.id}/insurance`);
      if (data.ok) toast.success(`Insurance submitted to MedexOva · policy ${data.policy_id || "pending"}`);
      else toast.error(errorDetailToText(data.error?.detail, "Insurance submission rejected"));
      onSaved?.();
    } catch (e) {
      toast.error(errorDetailToText(e?.response?.data?.detail, "Insurance submit failed"));
    } finally { setBusy(false); }
  };

  const uploadDoc = async (key, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Max 15 MB"); return; }
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await apiClient.post(`${TP}/coi-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(key, data.doc_id);
      toast.success("Uploaded");
    } catch (e) { toast.error(errorDetailToText(e?.response?.data?.detail, "Upload failed")); }
    finally { setUploading(null); }
  };

  // Group fields by section
  const bySection = {};
  spec.forEach(f => { (bySection[f.section] = bySection[f.section] || []).push(f); });

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="submission-drawer">
        <div className="sticky top-0 bg-white border-b border-[#E9EAE4] p-5 flex items-center justify-between z-10">
          <div>
            <div className="text-lg font-bold text-slate_navy">{record._display_name}</div>
            <div className="flex items-center gap-2 mt-1">
              <StateChip state={record._submission_state}/>
              {record._missing_count > 0 && <span className="text-xs text-amber-800">{record._missing_count} field(s) missing</span>}
            </div>
            <div className="mt-2">
              <MedexOvaTimeline row={record} statusText={(kind === "tp" ? record.medexova_tp_status : record.medexova_credentialing_status) || ""} testIdPrefix="drawer-medexova"/>
            </div>
            {kind === "driver" && (
              <div className="mt-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Insurance (Medex Group captive)</div>
                <MedexOvaTimeline row={record} statusText={record.medexova_insurance_status || ""} testIdPrefix="drawer-insurance"
                  sentKey="_ins_sent_at" receivedKey="_ins_received_at" errorKey="_ins_send_error" stalledKey="_ins_stalled"/>
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800 p-1.5"><X size={18}/></button>
        </div>

        {Object.entries(bySection).map(([sec, fields]) => (
          <div key={sec} className="p-5 border-b border-[#F1EFE8]">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{sec.replace(/_/g, " ")}</div>
            <div className="grid grid-cols-2 gap-3">
              {fields.map(f => {
                const isMissing = missing.has(f.key);
                const val = form[f.key] ?? "";
                const label = f.label + (f.required ? " *" : "");
                if (f.type === "upload") {
                  return (
                    <div key={f.key} className={`col-span-2 border rounded-md p-2 ${isMissing ? "border-amber-300 bg-amber-50" : "border-[#E2E0D8]"}`}>
                      <div className="text-[10px] font-semibold text-slate-700">{label}</div>
                      <input type="file" accept=".pdf,.png,.jpg,.jpeg" disabled={uploading === f.key}
                        onChange={(e) => uploadDoc(f.key, e.target.files?.[0])}
                        data-testid={`drawer-upload-${f.key}`}
                        style={{ fontSize: 11, marginTop: 4 }}/>
                      {uploading === f.key && <div className="text-[10px] text-slate-500">Uploading…</div>}
                      {val && <div className="text-[10px] text-green-700 font-semibold mt-1">✓ doc {String(val).slice(0, 12)}…</div>}
                    </div>
                  );
                }
                if (f.type === "bool") {
                  return (
                    <label key={f.key} className={`col-span-1 flex items-center gap-2 text-xs px-2 py-2 rounded-md border ${isMissing ? "border-amber-300 bg-amber-50" : "border-[#E2E0D8]"}`}>
                      <input type="checkbox" checked={!!val} onChange={(e) => set(f.key, e.target.checked)} data-testid={`drawer-${f.key}`}/>
                      {label}
                    </label>
                  );
                }
                if (f.type === "select") {
                  return (
                    <label key={f.key} className="col-span-1 block">
                      <div className={`text-[10px] uppercase tracking-wider mb-1 ${isMissing ? "text-amber-700 font-semibold" : "text-slate-500"}`}>{label}</div>
                      <select value={val} onChange={(e) => set(f.key, e.target.value)} data-testid={`drawer-${f.key}`}
                        className={`w-full border rounded-md px-2 py-1.5 text-xs ${isMissing ? "border-amber-300 bg-amber-50" : "border-[#E2E0D8]"}`}>
                        <option value="">— select —</option>
                        {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </label>
                  );
                }
                if (f.type === "multi") {
                  return (
                    <label key={f.key} className="col-span-2 block">
                      <div className={`text-[10px] uppercase tracking-wider mb-1 ${isMissing ? "text-amber-700 font-semibold" : "text-slate-500"}`}>{label} <span className="text-slate-400">(comma-sep)</span></div>
                      <input value={Array.isArray(val) ? val.join(", ") : val} onChange={(e) => set(f.key, e.target.value.split(",").map(s => s.trim()).filter(Boolean))} data-testid={`drawer-${f.key}`}
                        className={`w-full border rounded-md px-2 py-1.5 text-xs ${isMissing ? "border-amber-300 bg-amber-50" : "border-[#E2E0D8]"}`}
                        placeholder={(f.options || []).join(", ")}/>
                    </label>
                  );
                }
                return (
                  <label key={f.key} className="col-span-1 block">
                    <div className={`text-[10px] uppercase tracking-wider mb-1 ${isMissing ? "text-amber-700 font-semibold" : "text-slate-500"}`}>{label}</div>
                    <input value={val} onChange={(e) => set(f.key, e.target.value)}
                      type={f.type === "date" ? "date" : f.type === "number" ? "number" : f.type === "email" ? "email" : f.type === "tel" ? "tel" : "text"}
                      data-testid={`drawer-${f.key}`}
                      className={`w-full border rounded-md px-2 py-1.5 text-xs ${isMissing ? "border-amber-300 bg-amber-50" : "border-[#E2E0D8]"}`}
                      placeholder={f.hint || ""}/>
                    {f.hint && <div className="text-[9px] text-slate-400 mt-0.5">{f.hint}</div>}
                  </label>
                );
              })}
            </div>
          </div>
        ))}

        <div className="sticky bottom-0 bg-white border-t border-[#E9EAE4] p-4 flex gap-2 justify-end">
          <div className="mr-auto"><RequestDocsButton kind={kind === "tp" ? "tp" : "driver"} recordId={kind === "tp" ? record.tp_id : record.id} /></div>
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#E2E0D8] text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 rounded-md border border-[#E2E0D8] text-sm font-semibold hover:bg-slate-50" data-testid="drawer-save">
            {busy ? "Saving…" : "Save"}
          </button>
          <button onClick={submitToMedexOva} disabled={busy || record._missing_count > 0} className="px-4 py-2 rounded-md bg-forest text-white text-sm font-semibold disabled:opacity-50" data-testid="drawer-submit-medexova"
            title={record._missing_count > 0 ? "Fill in missing fields + Save first" : "Push to MedexOva"}>
            <Send size={12} className="inline mr-1"/> Submit to MedexOva
          </button>
          {kind === "driver" && (
            <button onClick={submitInsurance} disabled={busy} className="px-4 py-2 rounded-md border border-forest text-forest text-sm font-semibold disabled:opacity-50" data-testid="drawer-submit-insurance">
              <Send size={12} className="inline mr-1"/> Submit insurance
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
